/**
 * The Budget Workbench's on-screen seat filter, as pure functions so the screen,
 * the "export with filters" request and the tests all agree on what a filter means.
 * The server mirrors this in `workbench_pack_export.seat_matches_view`.
 */
import type { WorkbenchSeat } from '@/services/api/organizationService';

export type FlagFilter = 'all' | 'unfunded_staff' | 'funded_vacancy' | 'unbudgeted_vacant' | 'funded_filled';
export type RoleMode = 'include' | 'exclude';

/** The status chip a seat sits under. */
export const seatFlag = (s: WorkbenchSeat): FlagFilter => (s.flag as FlagFilter) || 'funded_filled';

/** The role a seat is grouped under on screen. */
export const roleOf = (s: WorkbenchSeat): string => s.title || '(untitled)';

/** Role picker: include keeps only the chosen roles, exclude hides them. No roles chosen = no filter. */
export function matchesRoles(s: WorkbenchSeat, roles: readonly string[], mode: RoleMode): boolean {
  if (!roles.length) return true;
  const inside = roles.includes(roleOf(s));
  return mode === 'include' ? inside : !inside;
}

export function matchesSearch(s: WorkbenchSeat, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (s.pn || '').toLowerCase().includes(q)
    || (s.incumbent || '').toLowerCase().includes(q)
    || s.fo_position_id.toLowerCase().includes(q)
    || roleOf(s).toLowerCase().includes(q)
    || `${s.campus || ''} ${s.department || ''} ${s.faculty || ''}`.toLowerCase().includes(q)
    || s.lines.some(l => l.code.toLowerCase().includes(q));
}

export function matchesView(
  s: WorkbenchSeat,
  view: { flag: FlagFilter; query: string; roles: readonly string[]; roleMode: RoleMode },
): boolean {
  return (view.flag === 'all' || seatFlag(s) === view.flag)
    && matchesRoles(s, view.roles, view.roleMode)
    && matchesSearch(s, view.query);
}

/** Every role in the slice with its seat count, alphabetical. */
export function roleOptions(seats: readonly WorkbenchSeat[]): Array<{ role: string; seats: number }> {
  const counts = new Map<string, number>();
  for (const s of seats) counts.set(roleOf(s), (counts.get(roleOf(s)) ?? 0) + 1);
  return [...counts.entries()].map(([role, n]) => ({ role, seats: n })).sort((a, b) => a.role.localeCompare(b.role));
}

export interface WorkbenchFilterState {
  campus: string; faculty: string; employment: string; includeAbolished: boolean;
  query: string; flag: FlagFilter; roles: readonly string[];
}

/** Human labels for every active filter (everything except the Department choice). */
export function activeFilterLabels(f: WorkbenchFilterState, roleMode: RoleMode): string[] {
  const out: string[] = [];
  if (f.campus) out.push(`Campus ${f.campus}`);
  if (f.faculty) out.push(`Faculty ${f.faculty}`);
  if (f.employment) out.push(`Type ${f.employment.split(',').filter(Boolean).join(', ')}`);
  if (f.includeAbolished) out.push('Abolished shown');
  if (f.roles.length) out.push(`${roleMode === 'include' ? 'Only' : 'Excluding'} ${f.roles.length} role${f.roles.length === 1 ? '' : 's'}`);
  if (f.flag !== 'all') out.push('Status chip');
  if (f.query.trim()) out.push(`Search "${f.query.trim()}"`);
  return out;
}
